import { Button } from "@msuite/ui/button";
import { Checkbox } from "@msuite/ui/checkbox";
import { useTranslation } from "@msuite/ui/localization";
import { type AsyncModalController, Modal } from "@msuite/ui/modal";
import { getHasAllRequiredAcknowledgements } from "@msuite/utils/attendances";
import type { AcknowledgementDefinitionsList } from "@msuite/utils/types";
import { useState } from "react";

/** Props Interface */
interface AttendanceAcknowledgementsModalProps {
	controller: AsyncModalController<string[]>;
	definitions: AcknowledgementDefinitionsList;
}

export function AttendanceAcknowledgementsModal({
	controller,
	definitions,
}: AttendanceAcknowledgementsModalProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** State */
	const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());

	/** Functions */
	function toggleId(id: string) {
		setCheckedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}

	/** Computed */
	const acknowledgedDefinitionIds = Array.from(checkedIds);
	const allChecked = getHasAllRequiredAcknowledgements(definitions, acknowledgedDefinitionIds);

	/** Render */
	return (
		<Modal
			isOpen={controller.isOpen}
			onClose={() => controller.onResolve(false)}
			title={t("users.required_acknowledgements")}
			withWarning={false}
		>
			<Modal.Body>
				<div className="v-stack gap-2">
					{definitions.map((definition) => (
						<button
							key={definition.id}
							type="button"
							onClick={() => toggleId(definition.id)}
							className="h-stack w-full items-start gap-3 cursor-pointer text-left"
						>
							<Checkbox
								value={checkedIds.has(definition.id)}
								onChange={() => toggleId(definition.id)}
							/>
							<span className="text-sm flex-1">{definition.text}</span>
						</button>
					))}
				</div>
			</Modal.Body>
			<Modal.Footer>
				<div className="self-end h-stack gap-4 ml-auto">
					<Button
						variant="ghost"
						onClick={() => {
							controller.onResolve(false);
						}}
					>
						{t("general.cancel")}
					</Button>
					<Button
						variant="primary"
						isDisabled={!allChecked}
						onClick={() => {
							controller.onResolve(acknowledgedDefinitionIds);
						}}
					>
						{t("general.confirm")}
					</Button>
				</div>
			</Modal.Footer>
		</Modal>
	);
}
